import { useState } from "react"
import { useParams } from "@tanstack/react-router"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Link } from "@tanstack/react-router"
import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Button, Input, Label, Dialog, DialogTitle } from "../components/ui"
import { useToast } from "../components/toast"
import { Shell } from "../components/shell"
import { Plus, Trash2, Pencil, Webhook, Lock } from "lucide-react"

const ALL_EVENTS = ["card:created", "card:updated", "card:deleted", "comment:created"] as const
type WebhookEvent = typeof ALL_EVENTS[number]

type WebhookRow = {
  id: string
  url: string
  events: string
  secret: string | null
  active: boolean
  createdAt: string
}

export default function WebhooksPage() {
  const { workspaceId } = useParams({ strict: false }) as { workspaceId: string }
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: session } = authClient.useSession()

  const [open, setOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [url, setUrl] = useState("")
  const [secret, setSecret] = useState("")
  const [selectedEvents, setSelectedEvents] = useState<WebhookEvent[]>(["card:created"])
  const [active, setActive] = useState(true)

  const { data: workspace } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: () => fetcher<{ role: string; plan: string }>(`/api/workspaces/${workspaceId}`),
    enabled: !!session,
  })

  const isAdmin = workspace?.role === "owner" || workspace?.role === "admin"
  const isPro = workspace?.plan === "pro" || workspace?.plan === "business"

  const { data: hooks, isLoading } = useQuery({
    queryKey: ["webhooks", workspaceId],
    queryFn: () => fetcher<WebhookRow[]>(`/api/webhooks?workspaceId=${workspaceId}`),
    enabled: !!workspace && isAdmin && isPro,
  })

  const resetForm = () => { setUrl(""); setSecret(""); setSelectedEvents(["card:created"]); setActive(true); setEditId(null) }

  const save = useMutation({
    mutationFn: (body: any) => editId
      ? fetcher(`/api/webhooks/${editId}`, { method: "PATCH", body: JSON.stringify(body) })
      : fetcher(`/api/webhooks?workspaceId=${workspaceId}`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhooks", workspaceId] })
      toast(editId ? "Webhook updated" : "Webhook created", "success")
      setOpen(false)
      resetForm()
    },
    onError: (err: any) => toast(err.message || "Failed to save webhook", "error"),
  })

  const remove = useMutation({
    mutationFn: (id: string) => fetcher(`/api/webhooks/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhooks", workspaceId] })
      toast("Webhook deleted", "success")
    },
    onError: (err: any) => toast(err.message || "Failed to delete webhook", "error"),
  })

  const openEdit = (hook: WebhookRow) => {
    setEditId(hook.id)
    setUrl(hook.url)
    setSecret(hook.secret || "")
    setActive(hook.active)
    let evs: WebhookEvent[] = []
    try { evs = JSON.parse(hook.events) } catch {}
    setSelectedEvents(evs)
    setOpen(true)
  }

  const toggleEvent = (ev: WebhookEvent) =>
    setSelectedEvents((prev) => prev.includes(ev) ? prev.filter((e) => e !== ev) : [...prev, ev])

  return (
    <Shell
      title="Webhooks"
      back={{ to: "/workspaces/$workspaceId", label: "Workspace" }}
      tabs={
        <div className="flex gap-4 text-sm">
          <Link to="/workspaces/$workspaceId" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Projects</Link>
          <Link to="/workspaces/$workspaceId/members" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Members</Link>
          <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Billing</Link>
          {isAdmin && (
            <Link to="/workspaces/$workspaceId/audit" params={{ workspaceId }} className="pb-2 text-muted-foreground hover:text-foreground">Audit log</Link>
          )}
          {isAdmin && (
            <span className="border-b-2 border-primary pb-2 font-medium">Webhooks</span>
          )}
        </div>
      }
      actions={
        isAdmin && isPro ? (
          <Button size="sm" onClick={() => { resetForm(); setOpen(true) }}>
            <Plus className="mr-1 h-4 w-4" /> New webhook
          </Button>
        ) : undefined
      }
    >
      <main className="mx-auto max-w-3xl p-6">
        {!isPro && (
          <div className="rounded-xl border border-primary/20 bg-gradient-to-r from-primary/10 to-primary/5 p-6 text-center">
            <Lock className="mx-auto mb-3 h-8 w-8 text-primary" />
            <h3 className="mb-1 font-semibold">Webhooks require a Pro or Business plan</h3>
            <p className="mb-4 text-sm text-muted-foreground">Get notified of card and comment events in real time via HTTP POST.</p>
            <Link to="/workspaces/$workspaceId/billing" params={{ workspaceId }}>
              <Button size="sm">Upgrade now</Button>
            </Link>
          </div>
        )}

        {isPro && isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

        {isPro && !isLoading && hooks?.length === 0 && (
          <div className="flex flex-col items-center py-16 text-center">
            <Webhook className="mb-4 h-10 w-10 text-muted-foreground" />
            <h3 className="mb-1 font-semibold">No webhooks yet</h3>
            <p className="mb-4 text-sm text-muted-foreground">Add a webhook to send HTTP POST requests when events happen.</p>
            <Button size="sm" onClick={() => { resetForm(); setOpen(true) }}><Plus className="mr-1 h-4 w-4" />New webhook</Button>
          </div>
        )}

        {isPro && hooks && hooks.length > 0 && (
          <div className="space-y-3">
            {hooks.map((hook) => {
              let evs: string[] = []
              try { evs = JSON.parse(hook.events) } catch {}
              return (
                <div key={hook.id} className="flex items-start justify-between gap-4 rounded-xl border border-border bg-card p-4 shadow-sm">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`h-2 w-2 rounded-full ${hook.active ? "bg-green-500" : "bg-muted-foreground"}`} />
                      <p className="truncate text-sm font-medium">{hook.url}</p>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {evs.map((e) => (
                        <span key={e} className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">{e}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button onClick={() => openEdit(hook)} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => confirm("Delete this webhook?") && remove.mutate(hook.id)} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </main>

      <Dialog open={open} onClose={() => { setOpen(false); resetForm() }}>
        <form onSubmit={(e) => { e.preventDefault(); save.mutate({ url, events: selectedEvents, secret: secret || undefined, active }) }} className="space-y-4">
          <DialogTitle>{editId ? "Edit webhook" : "New webhook"}</DialogTitle>
          <div>
            <Label>URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/hook" required type="url" />
          </div>
          <div>
            <Label>Secret (optional)</Label>
            <Input value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Used to sign payloads" />
          </div>
          <div>
            <Label>Events</Label>
            <div className="mt-1 space-y-1">
              {ALL_EVENTS.map((ev) => (
                <label key={ev} className="flex cursor-pointer items-center gap-2 text-sm">
                  <input type="checkbox" checked={selectedEvents.includes(ev)} onChange={() => toggleEvent(ev)} className="rounded" />
                  {ev}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="rounded" />
              Active
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" type="button" onClick={() => { setOpen(false); resetForm() }}>Cancel</Button>
            <Button type="submit" isLoading={save.isPending} disabled={selectedEvents.length === 0}>Save</Button>
          </div>
        </form>
      </Dialog>
    </Shell>
  )
}
